/**
 * Sentry Custom Metrics for E-commerce Tracking
 *
 * This module provides typed functions for pushing custom metrics to Sentry.
 * All metrics use counters with tags for proper segmentation in Sentry dashboards.
 *
 * Metric Naming Convention:
 * - Metric names use snake_case (e.g., "product_quantity_changed", "add_to_cart")
 * - Tag keys also use snake_case (e.g., "product_id", "vendor_name")
 *
 * Feature Flag:
 * All metric functions accept an `isEnabled` parameter that should be connected
 * to the `enableSentryCustomEvents` feature flag for controlled rollout.
 */

import type { Cart } from "@dransay/api/models";
import { logger } from "@dransay/logger";
import * as Sentry from "@sentry/react-router";

type MetricAttributes = Record<string, string | number | undefined | null>;

function toAttributes(
	obj: MetricAttributes,
): Record<string, string | number | boolean> {
	const result: Record<string, string | number | boolean> = {};
	for (const [key, value] of Object.entries(obj)) {
		if (value !== undefined && value !== null) {
			result[key] = typeof value === "number" ? value : String(value);
		}
	}
	return result;
}

function pushSentryMetric(
	metricName: string,
	attributes?: MetricAttributes,
	isEnabled = true,
): void {
	if (!isEnabled) {
		return;
	}

	try {
		Sentry.metrics.count(metricName, 1, {
			attributes: attributes ? toAttributes(attributes) : undefined,
		});
	} catch (error) {
		logger.error({ metricName, error }, "Sentry metric increment failed");
	}
}

export function pushProductQuantityChangedMetric(
	params: {
		productId: string;
		productName: string;
		previousQuantity: number;
		newQuantity: number;
		changeType: "increment" | "decrement";
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"product_quantity_changed",
		{
			product_id: params.productId,
			product_name: params.productName,
			previous_quantity: params.previousQuantity,
			new_quantity: params.newQuantity,
			change_type: params.changeType,
		},
		isEnabled,
	);
}

export function pushAddToCartMetric(
	params: {
		productId: string;
		productName: string;
		quantity: number;
		vendorId?: number | string;
		vendorName?: string | null;
		price?: number | null;
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"add_to_cart",
		{
			product_id: params.productId,
			product_name: params.productName,
			quantity: params.quantity,
			vendor_id: params.vendorId,
			vendor_name: params.vendorName,
			price: params.price,
		},
		isEnabled,
	);
}

export function pushCartQuantityChangedMetric(
	params: {
		productId: string;
		productName: string;
		previousQuantity: number;
		newQuantity: number;
		changeType: "increment" | "decrement";
		cartId: Cart["cartId"];
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"cart_quantity_changed",
		{
			product_id: params.productId,
			product_name: params.productName,
			previous_quantity: params.previousQuantity,
			new_quantity: params.newQuantity,
			change_type: params.changeType,
			cart_id: params.cartId,
		},
		isEnabled,
	);
}

export function pushCartDeliveryOptionsChangedMetric(
	params: {
		deliveryMethodUuid: string;
		deliveryMethodKind: string;
		vendorId?: number | string;
		vendorName?: string | null;
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"cart_delivery_options_changed",
		{
			delivery_method_uuid: params.deliveryMethodUuid,
			delivery_method_kind: params.deliveryMethodKind,
			vendor_id: params.vendorId,
			vendor_name: params.vendorName,
		},
		isEnabled,
	);
}

export function pushProceedToCheckoutMetric(
	params: {
		cartId: Cart["cartId"];
		totalItems: number;
		totalPrice: number;
		vendorId?: number | string;
		vendorName?: string | null;
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"proceed_to_checkout",
		{
			cart_id: params.cartId,
			total_items: params.totalItems,
			total_price: params.totalPrice,
			vendor_id: params.vendorId,
			vendor_name: params.vendorName,
		},
		isEnabled,
	);
}

export function pushCheckoutFailedMetric(
	params: {
		cartId: Cart["cartId"];
		errorMessage?: string;
		vendorId?: number | string;
		vendorName?: string | null;
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"checkout_failed",
		{
			cart_id: params.cartId,
			error_message: params.errorMessage,
			vendor_id: params.vendorId,
			vendor_name: params.vendorName,
		},
		isEnabled,
	);
}

export function pushCheckoutCartValidationErrorMetric(
	params: {
		cartId?: Cart["cartId"];
		errorCode: string;
		errorMessage: string;
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"checkout_cart_validation_error",
		{
			cart_id: params.cartId,
			error_code: params.errorCode,
			error_message: params.errorMessage,
		},
		isEnabled,
	);
}

export function pushCheckoutStockValidationErrorMetric(
	params: {
		cartId?: Cart["cartId"];
		productIds: string[];
		unavailableCount: number;
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"checkout_stock_validation_error",
		{
			cart_id: params.cartId,
			product_ids: params.productIds.join(","),
			unavailable_count: params.unavailableCount,
		},
		isEnabled,
	);
}

export function pushCheckoutAddressValidationErrorMetric(
	params: {
		fields: string[];
		errorMessages: string[];
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"checkout_address_validation_error",
		{
			fields: params.fields.join(","),
			error_messages: params.errorMessages.join("|"),
		},
		isEnabled,
	);
}

export function pushPaymentCompletedMetric(
	params: {
		orderId: string;
		paymentMethod: string;
		amount: number;
		currency: string;
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"payment_completed",
		{
			order_id: params.orderId,
			payment_method: params.paymentMethod,
			amount: params.amount,
			currency: params.currency,
		},
		isEnabled,
	);
}

export function pushPaymentFailedMetric(
	params: {
		orderId: string;
		paymentMethod?: string;
		resultCode?: string;
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"payment_failed",
		{
			order_id: params.orderId,
			payment_method: params.paymentMethod,
			result_code: params.resultCode,
		},
		isEnabled,
	);
}

export function pushPaymentErrorMetric(
	params: {
		orderId: string;
		errorName?: string;
		errorMessage?: string;
	},
	isEnabled: boolean,
): void {
	pushSentryMetric(
		"payment_error",
		{
			order_id: params.orderId,
			error_name: params.errorName,
			error_message: params.errorMessage,
		},
		isEnabled,
	);
}

/**
 * Push a TeleSign phone verification metric to Sentry.
 * Always enabled (no feature flag gating) since this is critical
 * observability for the gradual rollout of phone verification.
 */
export function pushTeleSignMetric(
	metricName:
		| "telesign_otp_send"
		| "telesign_otp_verify"
		| "telesign_age_verify",
	attributes: {
		outcome: string;
		user_id?: number;
		status_code?: number;
		dob_score?: number | null;
	},
): void {
	pushSentryMetric(metricName, attributes);
}
